import LevelNumberBadge from 'src/components/LevelNumberBadge';
import { defineMessages, useIntl } from 'react-intl';
import React, { KeyboardEvent, useMemo } from 'react';
import Link from 'next/link';
import { Icon } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import * as S from './styles';

interface ProgressLevel {
  className?: string;
  stage: string;
  languageName: string;
  currentLevel: number;
  minimumPoints: number;
  maximumPoints: number;
  totalPoints: number;
}

const msgs = defineMessages({
  currentLevelTitle: {
    id: 'homeScreen_languageLevel_web',
  },
  nextLevelTitle: {
    id: 'homeScreen_nextLanguageLevel_web',
  },
});

const ProgressLevel = ({
  className,
  stage,
  languageName,
  currentLevel,
  minimumPoints,
  maximumPoints,
  totalPoints,
}: ProgressLevel) => {
  const intl = useIntl();
  const { safePush } = useSafePush();

  const progressPercentage = useMemo(() => {
    const progressDiff = maximumPoints - minimumPoints;
    const receivedPoints = totalPoints - minimumPoints;

    return (receivedPoints / progressDiff) * 100;
  }, [minimumPoints, maximumPoints, totalPoints]);

  const onPressEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    /* istanbul ignore else: empty else branch */
    if (event?.key === 'Enter') {
      return safePush(ROUTES.myJourney);
    }
  };

  return (
    <Link href={ROUTES.myJourney} legacyBehavior>
      <S.Wrapper className={className} tabIndex={0} onKeyUp={onPressEnter} role="button">
        <S.CurrentStage>
          <Icon name="flag" />
          {stage}
          <S.ArrowIcon name="forward" />
        </S.CurrentStage>
        <S.Title role="heading" aria-level={3} data-testid="my_progress_skill_level_label">
          {intl.formatMessage(msgs.currentLevelTitle, { LanguageName: languageName })}{' '}
          <LevelNumberBadge level={currentLevel} state="in-progress" size="sm" />
        </S.Title>
        <S.ProgressContainer>
          <S.NextLevel>
            {intl.formatMessage(msgs.nextLevelTitle, { nextLevel: currentLevel + 1 })}
          </S.NextLevel>
          <S.ProgressBar percentComplete={progressPercentage} height="mediumSmall" />
          <S.CurrentPoints data-testid="my_progress_points_label">
            {totalPoints}/{maximumPoints}
          </S.CurrentPoints>
        </S.ProgressContainer>
      </S.Wrapper>
    </Link>
  );
};

export default ProgressLevel;
